import { useEffect } from "react";
import { createBrowserClient } from "@aicofi/sdk/client";
import { copy } from "./data";
import { LOCALE_STORAGE_KEY, resolveLocale, sanitizeNextPath } from "./lib/prefs";

/**
 * SPA OAuth / PKCE return screen.
 *
 * Unlike the Next.js products (which use a `/auth/callback` route handler), this
 * is a static Vite SPA: the Cloudflare Worker's SPA fallback serves index.html
 * for `/auth/callback`, and the
 * `@supabase/ssr` browser client auto-exchanges the `?code=` on load
 * (`detectSessionInUrl` + PKCE flow). We only need to wait for the session to be
 * established, then forward to the sanitised `?next` path so the user never
 * lingers on `/auth/callback`.
 */
function getCallbackLocale() {
  try {
    return resolveLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY), window.navigator.language);
  } catch {
    return resolveLocale(null, window.navigator.language);
  }
}

export function AuthCallback() {
  const locale = getCallbackLocale();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Same-app relative paths only (no protocol-relative `//host` escapes).
    const next = sanitizeNextPath(params.get("next"));

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.location.replace(next);
    };

    const supabase = createBrowserClient();
    if (!supabase) {
      finish();
      return;
    }

    // The browser client exchanges the code automatically; redirect once a
    // session exists (SIGNED_IN), with a getSession() check for the race where
    // the exchange already finished before this effect subscribed.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) finish();
    });
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) finish();
    });

    // Safety net: never hang on the callback screen (e.g. provider returned an
    // error instead of a code).
    const timer = window.setTimeout(finish, 5000);

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className="auth-callback-screen" role="status" aria-live="polite">
      <p>{copy[locale].authCompleting}</p>
    </div>
  );
}
